/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useState, useContext } from 'react';
import { connect, useSelector } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { parse } from 'qs';
import {
    object, shape, arrayOf, string, bool, func,
} from 'prop-types';
import { bindActionCreators } from 'redux';
import { trackEvent } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { getUserConfirmationForSubscriptionPopup } from '../../../../../state/ducks/App/App-Selectors';

import SubscriptionContainer from './SubscriptionContainer';

import { CrossMerch } from '../../CrossMerchContainer/CrossMerchContainer';
import { getBaseCode } from '../../../../helpers/tracking/product/dataLayerProductHelpers';
import { setSubscriptionDetail } from '../../../../../state/ducks/App/App-Actions';
import useSalesforcePersonalizedContentQuery from '../../../../helpers/Personalization/useSalesforcePersonalizedContentQuery';
import useClickStreamCustomEventsTracking from '../../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import { getCurrentPageType } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Selectors';
import SubscriptionSkeleton from '../../GraphqlSkeletonComponents/SubscriptionSkeleton';

const styles = (theme) => ({
    subcriptionCheckBoxContent: {
        fontSize: '14px',
        textAlign: 'center',
        '& b': {
            fontSize: '16px',
            display: 'block',
        },
    },
    subContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        '& > div': {
            margin: 'auto',
        },
    },
    checkbox: {
        color: theme.palette.cms?.checkbox,
        padding: '10px 20px',
        '&$checked': {
            color: theme.palette.cms?.checkbox,
            '&:hover': {
                backgroundColor: theme.palette.colorNeutral20,
            },
        },
        '&:hover': {
            backgroundColor: theme.palette.colorNeutral20,
        },
    },
    checked: {
        color: theme.palette.cms?.primary || theme.palette.colorPrimary,
        '&$checked': {
            color: theme.palette.cms?.primary || theme.palette.colorPrimary,
            '&:hover': {
                backgroundColor: theme.palette.colorNeutral20,
            },
        },
    },
});

const SubscriptionProductBannerUI = ({
    product,
    productBaseContent,
    triggerSubscriptionCheck,
    brand,
    subscriptionDetails,
    subscription,
    location,
    selectedSku,
    trackSubscriptionEvent,
    setSubscriptionDetailData,
    isPdpRedesignEnabled,
    isPdpRedesignShortEnabled,
}) => {
    const [selectedSkuHasAttributes, setSelectedSkuHasAttributes] = useState(null);
    const [newSubscriptionDetails, setNewSubscriptionDetails] = useState(subscriptionDetails);
    const { featureFlags = {}, presentation_family: presentationFamily = '' } = useContext(CrossMerch);
    const pageType = useSelector(getCurrentPageType);

    useEffect(() => {
        // use productSku level attribute to display checkbox
        setSelectedSkuHasAttributes(selectedSku?.attributes?.find((attr) => attr.name === 'subscription_type'));

        // sets subscriptionDetails with details from selectedSku
        setNewSubscriptionDetails({
            subscriptionType: selectedSku.subscriptionsList?.type?.[0] ? selectedSku.subscriptionsList?.type?.[0]?.toString() : '',
            subscriptionFrequency: selectedSku.subscriptionsList?.interval?.[0] ? selectedSku.subscriptionsList?.interval?.[0]?.toString() : '',
            subscriptionDuration: selectedSku.subscriptionsList?.duration?.[0] ?  selectedSku.subscriptionsList?.duration?.[0]?.toString() : '',
            subscriptionDurationKey: selectedSku.subscriptionsList?.duration?.[0] ? selectedSku.subscriptionsList?.duration?.[0]?.toString() : '',
        });

        // if selected sku doesn't have any subscription option
        if (!selectedSku.subscriptionsList?.type?.[0]) {
            // setting local state to empty (Used in Design)
            setNewSubscriptionDetails({
                subscriptionType: '',
                subscriptionFrequency: '',
                subscriptionDuration: '',
                subscriptionDurationKey: '',
            });
            // setting redux state to Empty (Used in Add to cart)
            setSubscriptionDetailData({
                duration: '',
                interval: '',
                subscriptionInterval: '',
                subscriptionType: null,
            });
            // deselecting check box
            triggerSubscriptionCheck(null, false, true);
        }
    }, [selectedSku]);

    useEffect(() => {
        // Track purchase option selection
        const purchaseOptionEventObj = {
            eventCategory: 'subscription',
            eventAction: 'impression',
            eventLabel: getBaseCode(product?.partNumber) || '',
            eventValue: '1',
            nonInteraction: true,
        };
        trackSubscriptionEvent(purchaseOptionEventObj);
    }, []);

    // AB TEST Hide and Show Subscription Block in PDP Design
    const { loading, variables, data: subscriptionData } = useSalesforcePersonalizedContentQuery({
        queryName: 'subscriptionBlock',
        interactionName: `${brand?.domain} - Get Campaign - Subscription Block`,
        featureFlag: featureFlags['is-pdp-redesign-subs-hidden-enabled'] && isPdpRedesignEnabled,
        optInHoldoutParticipation: false,
        resolveIdentity: false,
    });
    // CONTROL OR VARIANT(TEST) VALUE for Subscription Block  Sales Force Test
    const isSubscriptionBlock = subscriptionData?.contentSF?.campaign?.campaignResponses?.[0]?.payload?.userGroup;
    const subscriptionBlockEnabled = isSubscriptionBlock?.toLowerCase?.() === 'test';

    useClickStreamCustomEventsTracking({
        salesforceResponse: subscriptionData?.contentSF,
        isFireImpression: true,
        page: { type: pageType },
    });

    useEffect(() => {
        if (typeof subscriptionData !== 'undefined' && !window.wuSubscriptionEventFired && subscriptionData) {
            // GA tracking
            const eventParam = {
                eventCategory: 'Experiment',
                eventAction: 'Subscription Block Show and Hide',
                eventLabel: subscriptionBlockEnabled ? 'Variant' : 'Control',
                feature_detail: subscriptionBlockEnabled ? 'Variant' : 'Control',
                feature_element: 'Subscription Block Show and Hide',
                feature_category: 'Experiment',
            };
            trackEvent(eventParam);
            window.wuSubscriptionEventFired = true;
        }
        return () => {
            window.wuSubscriptionEventFired = false;
        };
    }, [subscriptionData]);

    if (loading || variables?.skip) {
        return <SubscriptionSkeleton />;
    }

    if (!selectedSku.subscriptionsList?.type?.[0] || (isPdpRedesignEnabled && subscriptionBlockEnabled)) {
        return <></>; // not returning subscription box
    }

    const productSubscriptionUrl = productBaseContent?.entries?.[0]?.subscriptions?.product_url ?? '';
    const productUrlIncludesSubscription = (product?.seo?.url || '').includes('subscription');
    const flowersProductHasSubscriptionAttributes = featureFlags['is-pdp-subscription-enabled'] && selectedSkuHasAttributes;
    const isFood_AndShouldRenderSubscriptionContainer = presentationFamily === 'food' && subscription?.viewSubscription && featureFlags['is-pdp-subscription-enabled'];
    const isFlower_AndShouldRenderSubscriptionContainer = presentationFamily === 'flower' && flowersProductHasSubscriptionAttributes && !location.pathname.includes('subscription');
    const subscriptionProperties = subscription;

    const searchQuery = parse(location?.search?.replace(/^\?/, '') || ''); // removing ? from the search string
    if (searchQuery?.subscription) {
        subscriptionProperties.subscriptionCheckbox = true; // setting checkbox checked if the url contains subscription param
        trackSubscriptionEvent({
            eventCategory: 'subscription',
            eventAction: 'product page',
            eventLabel: getBaseCode(product?.partNumber) || '',
            eventValue: parseInt(selectedSku?.prices[0]?.value, 10) || '',
        });
    }

    const showSubscriptionDetails = subscriptionDetails?.type ? subscriptionDetails : newSubscriptionDetails;
    if (isFood_AndShouldRenderSubscriptionContainer || isFlower_AndShouldRenderSubscriptionContainer) {
        return  (
            <SubscriptionContainer
                subscription={subscriptionProperties}
                subscriptionDetails={showSubscriptionDetails}
                triggerSubscriptionCheck={triggerSubscriptionCheck}
                brand={brand}
                selectedSku={selectedSku}
                partNumber={product?.partNumber}
                isPdpRedesignEnabled={isPdpRedesignEnabled}
                isPdpRedesignShortEnabled={isPdpRedesignShortEnabled}
            />
        );
    }
    if (!flowersProductHasSubscriptionAttributes) {
        return null;
    }
    if (presentationFamily !== 'flower'
        || !featureFlags['is-pdp-subscription-enabled']
        || !(flowersProductHasSubscriptionAttributes || productSubscriptionUrl || productUrlIncludesSubscription)) {
        return null;
    }

    return  (
        <SubscriptionContainer
            subscription={subscriptionProperties}
            subscriptionDetails={showSubscriptionDetails}
            triggerSubscriptionCheck={triggerSubscriptionCheck}
            brand={brand}
            selectedSku={selectedSku}
            isPdpRedesignEnabled={isPdpRedesignEnabled}
            isPdpRedesignShortEnabled={isPdpRedesignShortEnabled}
        />
    );
};

SubscriptionProductBannerUI.propTypes = {
    product: object.isRequired,
    location: shape({
        pathname: string.isRequired,
    }).isRequired,
    productBaseContent: shape({
        entries: arrayOf(shape({
            subscriptions: shape({
                product_url: string.isRequired,
            }).isRequired,
            url: string.isRequired,
        }).isRequired),
    }).isRequired,
    triggerSubscriptionCheck: func.isRequired,
    subscription: shape({
        subscriptionCheckbox: bool,
    }).isRequired,
    subscriptionDetails: shape({
        subscriptionType: string.isRequired,
        subscriptionDuration: string.isRequired,
        subscriptionFrequency: string.isRequired,
        subscriptionDurationKey: string.isRequired,
    }).isRequired,
    selectedSku: object.isRequired,
    brand: shape({
        domain: string,
    }).isRequired,
    trackSubscriptionEvent: func.isRequired,
    setSubscriptionDetailData: func.isRequired,
    isPdpRedesignEnabled: bool,
    isPdpRedesignShortEnabled: bool,
};
SubscriptionProductBannerUI.defaultProps = {
    isPdpRedesignEnabled: false,
    isPdpRedesignShortEnabled: false,
};
const mapDispatchToProps = (dispatch) => ({
    trackSubscriptionEvent: bindActionCreators(trackEvent, dispatch),
    setSubscriptionDetailData: bindActionCreators(setSubscriptionDetail, dispatch),
});

const mapStateToProps = (state) => ({
    isUserConfirmed: getUserConfirmationForSubscriptionPopup(state),
});

const styledSubscriptionProductBannerUI = withStyles(styles)(SubscriptionProductBannerUI);
export default connect(mapStateToProps, mapDispatchToProps)(styledSubscriptionProductBannerUI);
